import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Trophy, Home, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * GRANDE FINAL — public free-training landing (user request Feb-2026).
 *
 * Lists every COMPETITION game available in the current language and lets
 * the player start a round WITHOUT credentials and WITHOUT storing any
 * result (no ranking, no training_results, no competition_attempts). The
 * play flow reuses /play/start with `grande_final: true`, which the
 * backend handles by prefixing the session_id with `gf-` so /play/finish
 * also skips persistence.
 */
export default function GrandeFinalStart() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [cfg, setCfg] = useState({ enabled: false, name: "", subtitle: "" });
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cfgRes, gamesRes] = await Promise.all([
          api.get(`/grande-final/config?lang=${encodeURIComponent(lang)}`),
          api.get(`/grande-final/games?lang=${encodeURIComponent(lang)}`),
        ]);
        setCfg({
          enabled: !!cfgRes?.data?.enabled,
          name: cfgRes?.data?.name || "",
          subtitle: cfgRes?.data?.subtitle || "",
        });
        setGames(Array.isArray(gamesRes?.data) ? gamesRes.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Falhou a carregar os jogos");
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  // Guard: when the admin flips the toggle off AFTER someone shares the
  // link, send them home with a brief toast instead of showing an empty
  // page.
  useEffect(() => {
    if (!loading && !cfg.enabled) {
      toast.info("Esta zona está desligada de momento.");
      nav("/", { replace: true });
    }
  }, [loading, cfg.enabled, nav]);

  const startGame = async (g, tier) => {
    const tierId = tier?.id || null;
    const tierLevel = tier && tier.level != null ? parseInt(tier.level, 10) : null;
    setStarting(`${g.id}:${tierId || ""}`);
    try {
      // Create (or reuse) a lightweight anonymous training_user_id so the
      // GamePlay screen can call /play/start with `grande_final=true`.
      let trainingUserId = sessionStorage.getItem("training_user_id");
      if (!trainingUserId) {
        trainingUserId = localStorage.getItem("training_user_id");
      }
      if (!trainingUserId) {
        const initRes = await api.post("/training/init");
        trainingUserId = initRes?.data?.training_user_id;
      }
      if (trainingUserId) {
        sessionStorage.setItem("training_user_id", trainingUserId);
        localStorage.setItem("training_user_id", trainingUserId);
      }
      // Grande Final is "no profile" — stub minimal values so GamePlay
      // doesn't bail out of /play/start. These are NEVER persisted on
      // the backend thanks to the `gf-` session_id prefix.
      sessionStorage.setItem("player_name", "Jogador");
      sessionStorage.setItem("player_nationality", "PT");
      sessionStorage.removeItem("player_school");
      if (tierId) {
        sessionStorage.setItem("player_tier_id", tierId);
      } else {
        sessionStorage.removeItem("player_tier_id");
      }
      // The roulette difficulty is driven by `player_level` / URL `?level=`.
      // Grande Final tiers carry their own level (e.g. "Escalão 4" = Nível 3),
      // so propagate it — otherwise GamePlay defaults to level 1 and the
      // round always plays at the lowest escalão.
      if (tierLevel && tierLevel >= 1) {
        sessionStorage.setItem("player_level", String(tierLevel));
      } else {
        sessionStorage.removeItem("player_level");
      }
      const qs = new URLSearchParams({ mode: "training", gf: "1" });
      if (tierId) qs.set("tier", tierId);
      if (tierLevel && tierLevel >= 1) qs.set("level", String(tierLevel));
      nav(`/jogar/${g.id}?${qs.toString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a iniciar o jogo");
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100 pb-20">
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => nav("/")}
            className="rounded-full"
            data-testid="gf-back-home"
          >
            <Home className="w-4 h-4 mr-2" /> {t("home_btn")}
          </Button>
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg mb-3">
            <Trophy className="w-9 h-9 text-white" />
          </div>
          <h1 className="font-[Fredoka] font-black text-amber-900 text-4xl md:text-5xl uppercase tracking-wide">
            {cfg.name || "Grande Final"}
          </h1>
          {cfg.subtitle && (
            <p className="text-amber-800 mt-2 text-base md:text-lg font-medium">
              {cfg.subtitle}
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-12">A carregar jogos…</div>
        ) : games.length === 0 ? (
          <div className="text-center text-slate-600 py-12 bg-white rounded-2xl shadow-sm">
            Ainda não há jogos de competição disponíveis para este modo.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gf-games-grid">
            {games.map((g) => (
              <GameCard
                key={g.id}
                g={g}
                lang={lang}
                starting={starting}
                onStart={startGame}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GameCard({ g, lang, starting, onStart }) {
  const tiers = Array.isArray(g.tiers) ? g.tiers.filter((tr) => tr) : [];
  const key = (tid) => `${g.id}:${tid || ""}`;
  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
      data-testid={`gf-game-card-${g.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-[Fredoka] font-black text-slate-800 text-lg leading-tight">
          {g.name}
        </h3>
      </div>
      {tiers.length > 0 ? (
        <div className="grid gap-2">
          {tiers.map((tr) => (
            <Button
              key={tr.id}
              onClick={() => onStart(g, tr)}
              disabled={starting === key(tr.id)}
              className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold justify-between"
              data-testid={`gf-start-${g.id}-${tr.id}`}
            >
              <span>{tr.name}</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          ))}
        </div>
      ) : (
        <Button
          onClick={() => onStart(g, null)}
          disabled={starting === key(null)}
          className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold"
          data-testid={`gf-start-${g.id}`}
        >
          Jogar <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}
